import { useSelector } from "react-redux";
import { useEffect, useState, useRef } from "react";
import { RootState } from "../../../appRedux/store";
import useValue from "../../../hooks/useValue";
import * as dashboard from "../../../services/dashboard";
import { CustomNotification } from "../../../util/CustomNotification";
import type { KeyFobDetails, MergedData } from "../../../shared/interface/dashboard.interface";

export const useParkingStatusCard = (props: { data: MergedData[] }) => {
  /* STATE */
  const [keyFobId, setKeyFobId] = useState<number | null>(null);
  const [keyFobDetails, setKeyFobDetails] = useState<KeyFobDetails | null>(null);
  const [activeKeys, setActiveKeys] = useState<string | string[]>(["1"]);
  const [popoverVisible, setPopoverVisible] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const lastSuccessfulSearch = useRef<string>("");

  const { masterFilters } = useSelector(({ common }: RootState) => common);

  /* CUSTOM HOOKS */
  const { value: loading, handleDisableValue: stopLoading, handleEnableValue: startLoading } = useValue(false);
  const { value: isResyncModalOpen, handleDisableValue: handleDisableResyncModal, handleEnableValue: handleEnableResyncModal } = useValue(false);

  /* HANDLERS */
  const handlePopoverOpenChange = (id: number, open: boolean) => {
    setKeyFobId(null);
    setKeyFobId(open ? id : null);
  };

  const handlePopoverOpenChangeBySearch = (open: boolean) => {
    setKeyFobDetails(null);
    setPopoverVisible(false);
  };

  const handleChange = (key: string | string[]) => {
    setActiveKeys(key);
  };

  const handleShowAll = (length: number) => {
    const allKeys = Array.from({ length }, (_, index) => (index + 1).toString());
    setActiveKeys(activeKeys.length === allKeys.length ? ["1"] : allKeys);
  };

  const handleClose = () => {
    setKeyFobId(null);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  /* Function to get KeyFob details by search text */
  const handleSearchAndScroll = async (searchQuery: string) => {
    // Check if the value contains any non-alphanumeric characters
    const hasNonAlphanumeric = /[^a-zA-Z0-9]/.test(searchQuery);

    if (hasNonAlphanumeric) {
      // Throw an error if the input contains special characters
      CustomNotification.showErrorMessage("Only alphanumeric characters (A-Z, a-z, 0-9) are allowed.");
      return;
    }

    try {
      if (!searchQuery) return;
      if (!masterFilters?.customerId || !masterFilters?.locationId) return;

      startLoading();
      const response = await dashboard.getKeyFobDetailsBySearchQuery(searchQuery, masterFilters?.customerId, masterFilters?.locationId);
      const data = response?.data;

      if (!data) {
        CustomNotification.showErrorMessage("No data found.");
        return;
      }

      // Find the section index based on the key_fob_token
      let sectionIndex: string | undefined;
      let matchingKeyFobId: number | null = null;

      props?.data?.forEach((section, index) => {
        section.keyfobList.forEach((keyFob) => {
          if (keyFob.keyFob === data.key_fob_token || String(keyFob.keyFob) === searchQuery) {
            sectionIndex = (index + 1).toString();
            matchingKeyFobId = keyFob.id;
          }
        });
      });

      if (!matchingKeyFobId) {
        CustomNotification.showErrorMessage("Record not found.");
        return;
      }

      // Format the data with the relevant details
      const formattedData = {
        ...data,
        username: `${data?.keyFobAllocation?.[0]?.mobileUser?.first_name ?? ""} ${data?.keyFobAllocation?.[0]?.mobileUser?.last_name ?? ""}`,
        vehicleNumber: data?.keyFobAllocation?.[0]?.vehicle_number,
        sectionIndex
      };

      setKeyFobDetails(formattedData);
      lastSuccessfulSearch.current = searchQuery;

      if (sectionIndex) {
        setActiveKeys([sectionIndex]);
        scrollToSection(`section-${sectionIndex}`);
      }

      if (matchingKeyFobId) {
        setKeyFobId(matchingKeyFobId);
        setTimeout(() => {
          const element = document.querySelector(`[data-keyfob-id="${matchingKeyFobId}"]`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        });
      }
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      stopLoading();
    }
  };

  const handleChangeSearch = (value: string) => {
    const trimmedValue = value.replace(/^\s+/, "");
    setSearchValue(trimmedValue);

    // Close popover if the current input differs from the last successful search
    if (trimmedValue !== lastSuccessfulSearch.current) {
      setPopoverVisible(false);
      setKeyFobId(null);
      setKeyFobDetails(null);
    }
  };

  /* EFFECTS */
  useEffect(() => {
    const fetchData = async (keyFobId: number) => {
      try {
        startLoading();
        const response = await dashboard.getKeyFobDetailsById(keyFobId);
        setKeyFobDetails(response?.data);
      } catch (error) {
        CustomNotification.showErrorMessage(error);
      } finally {
        stopLoading();
      }
    };

    if (keyFobId) fetchData(keyFobId);
  }, [keyFobId]);

  return {
    keyFobId,
    setKeyFobId,
    handlePopoverOpenChange,
    handleChange,
    keyFobDetails,
    loading,
    handleClose,
    handleShowAll,
    activeKeys,
    handlePopoverOpenChangeBySearch,
    popoverVisible,
    handleSearchAndScroll,
    handleChangeSearch,
    searchValue,
    isResyncModalOpen,
    handleDisableResyncModal,
    handleEnableResyncModal
  };
};
